import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs/operators';
import { AuthService } from 'src/app/core/http-services/auth.service';
import { AzureEndpointsService } from 'src/app/core/http-services/azure-endpoints.service';
import { AzureOperationsService } from 'src/app/core/services/azure-operations.service';
import { AzureTokenService } from 'src/app/core/services/azure-token.service';
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service';
import { PAGE_RESULT_SIZE } from 'src/app/shared/constants';
import { AzureEndpointDisplay } from 'src/app/shared/models/azure-endpoints.model';
import { AzureErrorResponse } from 'src/app/shared/models/azure-token.model';
import { Pagination } from 'src/app/shared/models/pagination.model';
import { SpecialRoles } from 'src/app/shared/models/signin.model';
import { GeneralFunctions } from '../../shared/general-functions';
import { EditEndpointComponent } from '../edit-endpoint/edit-endpoint.component';

@Component({
  selector: 'app-manage-endpoints',
  templateUrl: './manage-endpoints.component.html',
  styleUrls: ['./manage-endpoints.component.css']
})
export class ManageEndpointsComponent implements OnInit {
  isShowDetails: boolean = false;
  pageNo: number = 0;
  pager: Pagination = new Pagination();
  endpoints: AzureEndpointDisplay[] = new Array();
  selectedEndpoint: AzureEndpointDisplay = null;
  totalRecords: number = 0;
  successMessage: string = '';
  errorMessage: string = '';
  isShowSuccess: boolean = false;
  isShowError: boolean = false;
  roles: SpecialRoles;
  filterId: number = 0;
  filterInput: string = '';
  isFilterEntered: boolean = true;
  actualFilterInput: string = '';

  constructor(private azureEndpointService: AzureEndpointsService,
    private modalService: NgbModal,
    private generalFunctions: GeneralFunctions,
    private authService: AuthService,
    private azureOperationsService: AzureOperationsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private azureTokenService: AzureTokenService,
    private confirmationDialogService: ConfirmationDialogService) { }

  ngOnInit(): void {
    // Note: Below 'queryParams' can be replaced with 'params' depending on your requirements
    this.activatedRoute.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.filterId = id;
      }

    });

    this.roles = this.authService.getSpecialRoles();
    this.getNextRecordsBatch(true, true);
  }

  getNextRecordsBatch(isForwardMove: boolean = true, selectARow = false) {
    if (isForwardMove) {
      this.pageNo++;
    }
    else {
      this.pageNo--;
      if (this.pageNo == 0) {
        this.pageNo = 1;
      }
    }

    if (this.filterId == 0) {
      this.azureEndpointService.getRecordCounts(this.actualFilterInput)
        .subscribe((data: number) => {
          this.totalRecords = data;
          this.pager = this.generalFunctions.paginate(this.totalRecords, this.pageNo, PAGE_RESULT_SIZE, 5);

          if (data > 0) {
            this.azureEndpointService.getEndpoints(this.pageNo, this.actualFilterInput)
              .subscribe((dataList: AzureEndpointDisplay[]) => {

                if (dataList && dataList.length > 0) {
                  this.endpoints = dataList;

                  if (selectARow) {
                    this.selectedEndpoint = null;
                    this.selectedEndpoint = this.endpoints[0];
                    this.isShowDetails = true;
                  }
                }
              });
          }
        });
    }
    else {
      this.azureEndpointService.getEndpointById(this.filterId)
        .subscribe((data: AzureEndpointDisplay) => {
          if (data) {
            this.endpoints.push(data);
            this.selectedEndpoint = data;
            this.isShowDetails = true;
          }
        })
    }

  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  deleteAzureEndpointOnMicrosoft(id: number) {
    var endpoint = this.endpoints.find(x => x.Id == id);
    if (endpoint) {
      this.azureOperationsService.deleteAzureHybridConnection(
        endpoint.SubscriptionId,
        endpoint.ResourceGroup,
        endpoint.AzureNamespace,
        endpoint.EndpointName)
        .pipe(first())
        .subscribe(result => {
          this.isShowSuccess = true;
          this.successMessage = "Endpoint deleted successfully";
          this.refreshRecords();
        },
          (error: AzureErrorResponse) => {
            this.isShowError = true;
            this.errorMessage = error.message;
          });
    }
  }

  openModal(id: number = 0) {
    if (this.azureTokenService.isLoggedIn()) {
      //USER IS ALREADY LOGGED IN
      this.openModelToAddEdit(id);
    }
    else {
      this.azureTokenService.login().then(() => {
        this.openModelToAddEdit(id);
      }).catch((err) => {
        this.isShowError = true;
        this.errorMessage = "Could not log you into Azure account, check developer console for more details";
      });
    }
  }

  openModelToAddEdit(id: number = 0) {
    const modalRef = this.modalService.open(EditEndpointComponent, { windowClass: 'modal-holder', centered: true });
    modalRef.componentInstance.azureEndpointId = id;
    if (id > 0) {
      var curEndpoint = this.endpoints.find(a => a.Id === id);
      if (curEndpoint) {
        modalRef.componentInstance.currentEndpoint = curEndpoint;
        modalRef.componentInstance.setFormValues();
      }
    }

    /*
    * | FOLLOWING WILL EMIT TO SUGGEST
    * | NAMESPACE IS CREATED OR UPDATED SUCCESSFULLY
    */
    modalRef.componentInstance.isEndpointSaved.subscribe((value) => {
      this.isShowSuccess = true;
      this.successMessage = "Azure endpoint saved/updated successfully";
      this.refreshRecords();
    });
  }

  closeSuccess() {
    this.isShowSuccess = false;
  }

  closeError() {
    this.isShowError = false;
  }

  refreshRecords() {
    if (this.pageNo == 0) {
      this.pageNo = 1;
    }

    this.azureEndpointService.getRecordCounts()
      .subscribe((data: number) => {
        this.totalRecords = data;
        this.pager = this.generalFunctions.paginate(this.totalRecords, this.pageNo, PAGE_RESULT_SIZE, 5);

        if (data > 0) {
          this.azureEndpointService.getEndpoints(this.pageNo)
            .subscribe((dataList: AzureEndpointDisplay[]) => {
              if (dataList && dataList.length > 0) {
                this.endpoints = dataList;
              }
            });
        }
      });
  }

  fetchSpecificPage(pgNo: number) {
    if (pgNo == this.pageNo) {
      return;
    }

    this.pageNo = pgNo;
    this.refreshRecords();
  }

  deleteEndpoint(id: number) {
    this.confirmationDialogService.confirm('Please confirm', 'Are you sure you want to delete this endpoint?')
      .then((confirmed) => {
        if (confirmed) {
          this.azureTokenService.login().then(() => {
            this.azureEndpointService.deleteEndpoint(id)
              .subscribe((data: boolean) => {
                if (data) {
                  this.refreshRecords();
                  this.deleteAzureEndpointOnMicrosoft(id);
                }
                else {
                  this.isShowError = true;
                  this.errorMessage = "Something went wrong";
                }
              });
          }).catch((err) => {
            this.isShowError = true;
            this.errorMessage = "Could not log you into Azure account, check developer console for more details";
          });
        }
        else {
          return;
        }
      }).catch((error) => {
        return;
      })
  }

  showDetails(id: number, isShow = true) {
    if (!this.selectedEndpoint) {
      this.isShowDetails = isShow;
      if (isShow) {
        this.selectedEndpoint = this.endpoints.find(x => x.Id == id);
      }
    }
    else {


      if (id == this.selectedEndpoint.Id) {
        this.isShowDetails = false;
        this.selectedEndpoint = null;
        return;
      }
      this.isShowDetails = isShow;
      if (isShow) {
        this.selectedEndpoint = this.endpoints.find(x => x.Id == id);
      }
    }

    if (!this.isShowDetails)
      this.selectedEndpoint = null;

  }

  closeDetails() {
    this.isShowDetails = false;
    this.selectedEndpoint = null;
  }

  removeFilter() {
    if (this.filterId > 0) {
      this.filterId = 0;
      this.router.navigate(['/endpoints']);
      this.getNextRecordsBatch(false, true);
    }
    else {
      this.pageNo = 0;
      this.actualFilterInput = '';
      this.filterInput = '';
      this.isFilterEntered = true;
      this.getNextRecordsBatch(true, true);
    }
  }

  doFilter() {
    this.actualFilterInput = '';

    if (!this.filterInput) {
      this.isFilterEntered = false;
      return
    }
    else
      this.isFilterEntered = true;

    this.pageNo = 0;
    this.actualFilterInput = this.filterInput;
    this.getNextRecordsBatch(true, true);
  }
}
